import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { scheduleApi, userApi } from '../../services/api';
import { useAppStore } from '../../store/appStore';
import { fromZonedTime, formatInTimeZone } from 'date-fns-tz';
import { colorFor } from '../common/Avatar';

const HALF_HOUR = 30 * 60 * 1000;
const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Replicates backend canAccessCamera so the "booking as" picker only lists operators
// who can actually be assigned to the selected camera.
function canAccess(user, cam) {
  if (!user || !cam) return false;
  if (user.role === 'admin') return true;
  if (user.role === 'volunteer_operator') return Array.isArray(user.allowedCameras) && user.allowedCameras.includes(cam.id);
  return cam.companyId != null && cam.companyId === user.companyId;
}

const pad = (n) => String(n).padStart(2, '0');

// 12-hour label, e.g. h=19,m='00' -> "7:00 PM"; h=0 -> "12:00 AM"
const fmt12 = (h, m) => {
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m} ${ampm}`;
};

export default function SchedulePanel() {
  const { user, cameras, addToast } = useAppStore();
  const accessibleCameras = cameras; // GET /cameras is already access-filtered server-side
  const [cameraId, setCameraId] = useState(accessibleCameras[0]?.id || '');
  const [weekOffset, setWeekOffset] = useState(0);
  const [slots, setSlots] = useState([]); // slotStartIso -> handled via map below
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [bookAs, setBookAs] = useState(''); // '' = me (managers only)

  const camera = accessibleCameras.find(c => c.id === cameraId) || null;
  const isManager = user?.role === 'admin' || user?.role === 'customer_admin';
  const viewerTz = user?.timezone || browserTz;
  const cameraTz = camera?.timezone || viewerTz;

  // Build the 7 day-columns for the visible week, expressed in the CAMERA timezone.
  const days = useMemo(() => {
    if (!camera) return [];
    const todayStr = formatInTimeZone(new Date(), cameraTz, 'yyyy-MM-dd'); // camera-local date
    const anchor = new Date(todayStr + 'T12:00:00Z'); // noon UTC avoids date rollover
    anchor.setUTCDate(anchor.getUTCDate() + weekOffset * 7);
    const dow = anchor.getUTCDay(); // 0=Sun..6=Sat
    anchor.setUTCDate(anchor.getUTCDate() + (dow === 0 ? -6 : 1 - dow)); // back to Monday
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(anchor);
      d.setUTCDate(d.getUTCDate() + i);
      const dateStr = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
      const label = d.toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' });
      const dm = `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
      const isToday = dateStr === todayStr;
      return { dateStr, label, dm, isToday };
    });
  }, [camera, cameraTz, weekOffset]);

  // Convert a camera-local day + half-hour index to its UTC slot-start ISO.
  const slotIso = useCallback((dateStr, rowIdx) => {
    const h = Math.floor(rowIdx / 2), m = rowIdx % 2 === 0 ? '00' : '30';
    return fromZonedTime(`${dateStr} ${pad(h)}:${m}:00`, cameraTz).toISOString();
  }, [cameraTz]);

  const load = useCallback(async () => {
    if (!cameraId || days.length === 0) return;
    setLoading(true);
    try {
      const from = slotIso(days[0].dateStr, 0);
      const to = new Date(new Date(slotIso(days[6].dateStr, 47)).getTime() + HALF_HOUR).toISOString();
      const res = await scheduleApi.list(cameraId, from, to);
      setSlots(res.data.slots || []);
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to load schedule' });
    } finally {
      setLoading(false);
    }
  }, [cameraId, days, slotIso, addToast]);

  useEffect(() => { load(); }, [load]);

  // Managers: load operators for the "booking as" picker.
  useEffect(() => {
    if (!isManager) return;
    userApi.list().then(res => setUsers(res.data || [])).catch(() => {});
  }, [isManager]);

  const slotByStart = useMemo(() => {
    const m = new Map();
    slots.forEach(s => m.set(new Date(s.slotStart).toISOString(), s));
    return m;
  }, [slots]);

  // --- booking interactions (click + drag-select) ---
  const dragging = useRef(false);
  const [selected, setSelected] = useState(() => new Set());

  const commitSelection = useCallback(async (keys) => {
    if (keys.length === 0) return;
    try {
      const res = await scheduleApi.book(cameraId, keys, bookAs || undefined);
      if (res.data.skipped?.length) {
        addToast({ type: 'warning', message: `${res.data.skipped.length} slot(s) were already taken` });
      }
      await load();
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to book' });
    }
  }, [cameraId, bookAs, load, addToast]);

  useEffect(() => {
    function onUp() {
      if (!dragging.current) return;
      dragging.current = false;
      setSelected(sel => { if (sel.size) commitSelection([...sel]); return new Set(); });
    }
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
  }, [commitSelection]);

  async function removeSlot(slot) {
    try {
      await scheduleApi.remove(slot.id);
      await load();
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to remove' });
    }
  }

  function onCellDown(key, slot, mine) {
    if (slot) { if (mine || isManager) removeSlot(slot); return; }
    dragging.current = true;
    setSelected(new Set([key]));
  }
  function onCellEnter(key, slot) {
    if (dragging.current && !slot) setSelected(sel => new Set(sel).add(key));
  }

  // Operators selectable for "booking as" on this camera.
  const bookableOperators = useMemo(
    () => users.filter(u => u.id !== user?.id && canAccess(u, camera)),
    [users, camera, user?.id]
  );

  const rows = Array.from({ length: 48 }, (_, i) => i);

  // Per-day column: the slot in each row, plus which row should carry the operator's
  // name (the middle row of each contiguous booked block, so the name is centered in
  // the block rather than repeated in every 30-min cell).
  const dayColumns = useMemo(() => days.map(d => {
    const col = rows.map(r => slotByStart.get(slotIso(d.dateStr, r)) || null);
    const labelRow = new Array(48).fill(false);
    let i = 0;
    while (i < 48) {
      if (col[i]) {
        let j = i;
        while (j + 1 < 48 && col[j + 1] && col[j + 1].operatorId === col[i].operatorId) j++;
        labelRow[Math.floor((i + j) / 2)] = true;
        i = j + 1;
      } else i++;
    }
    return { col, labelRow };
  }), [days, slotByStart, slotIso]); // eslint-disable-line react-hooks/exhaustive-deps

  // Repeat the effective operator's bookings in the visible week onto following weeks,
  // preserving the camera-local weekday + time.
  async function repeatWeek() {
    const effId = bookAs || user?.id;
    const mineSlots = slots.filter(s => s.operatorId === effId);
    if (mineSlots.length === 0) { addToast({ type: 'info', message: 'No bookings in this week to repeat' }); return; }
    const nStr = window.prompt('Repeat this week’s bookings for how many following weeks? (1–12)', '4');
    if (!nStr) return;
    const n = Math.max(1, Math.min(12, parseInt(nStr, 10) || 0));
    if (!n) return;
    const newStarts = [];
    for (let k = 1; k <= n; k++) {
      for (const s of mineSlots) {
        const local = formatInTimeZone(s.slotStart, cameraTz, "yyyy-MM-dd'T'HH:mm");
        const [datePart, timePart] = local.split('T');
        const anchor = new Date(datePart + 'T12:00:00Z');
        anchor.setUTCDate(anchor.getUTCDate() + 7 * k);
        const nd = `${anchor.getUTCFullYear()}-${pad(anchor.getUTCMonth() + 1)}-${pad(anchor.getUTCDate())}`;
        newStarts.push(fromZonedTime(`${nd} ${timePart}:00`, cameraTz).toISOString());
      }
    }
    try {
      let made = 0, taken = 0;
      for (let i = 0; i < newStarts.length; i += 200) {
        const res = await scheduleApi.book(cameraId, newStarts.slice(i, i + 200), bookAs || undefined);
        made += res.data.booked?.length || 0;
        taken += res.data.skipped?.length || 0;
      }
      addToast({ type: 'success', message: `Repeated: ${made} slot(s) added${taken ? `, ${taken} already booked` : ''}` });
      await load();
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Repeat failed' });
    }
  }

  const cap = (s) => (s ? s.split(' ').filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '');

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '16px', gap: '12px' }}>
      {/* Header / controls */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, gap: '10px', flexWrap: 'wrap' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 700, letterSpacing: '0.1em', color: 'var(--accent-cyan)', textTransform: 'uppercase' }}>
          Schedule
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select className="input-field select-field" value={cameraId} onChange={e => { setCameraId(e.target.value); setWeekOffset(0); }} style={{ minWidth: '200px' }}>
            {accessibleCameras.length === 0 && <option value="">No cameras available</option>}
            {accessibleCameras.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {isManager && bookableOperators.length > 0 && (
            <select className="input-field select-field" value={bookAs} onChange={e => setBookAs(e.target.value)} title="Book slots on behalf of">
              <option value="">Booking as: Me</option>
              {bookableOperators.map(o => <option key={o.id} value={o.id}>Booking as: {o.username}</option>)}
            </select>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => setWeekOffset(w => w - 1)}>‹ Prev</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setWeekOffset(0)}>Today</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setWeekOffset(w => w + 1)}>Next ›</button>
          <button className="btn btn-primary btn-sm" onClick={repeatWeek} title="Copy this week's bookings to following weeks">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 014-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 01-4 4H3"/></svg>
            Repeat
          </button>
        </div>
      </div>

      {camera && (
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '13px', color: 'var(--text-secondary)', flexShrink: 0 }}>
          {days.length > 0 && <><strong style={{ color: 'var(--text-primary)' }}>Week of {days[0].label} {days[0].dm} – {days[6].label} {days[6].dm}</strong> · </>}
          Times in camera local time (<span style={{ color: 'var(--accent-cyan)', fontWeight: 600 }}>{cameraTz}</span>)
          {viewerTz !== cameraTz && <> · your time: <span style={{ color: 'var(--text-primary)' }}>{viewerTz}</span></>}
          {!camera.timezone && <span style={{ color: 'var(--accent-orange)' }}> · camera has no timezone set — using yours</span>}
          {loading && <span> · loading…</span>}
        </div>
      )}

      {!camera ? (
        <div style={{ color: 'var(--text-muted)', padding: '40px', textAlign: 'center', fontFamily: 'var(--font-display)' }}>Select a camera to view its schedule.</div>
      ) : (
        <div className="scrollable" style={{ flex: 1, overflow: 'auto', border: '1px solid var(--border-dim)', borderRadius: 'var(--radius-md)', userSelect: 'none' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: '62px', position: 'sticky', left: 0, zIndex: 3 }} />
                {days.map(d => (
                  <th key={d.dateStr} style={{ ...thStyle, color: d.isToday ? 'var(--accent-cyan)' : 'var(--text-secondary)' }}>
                    {d.label}<br /><span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>{d.dm}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(rowIdx => {
                const h = Math.floor(rowIdx / 2), m = rowIdx % 2 === 0 ? '00' : '30';
                const onHour = rowIdx % 2 === 0;
                return (
                  <tr key={rowIdx}>
                    <td style={{ ...timeCellStyle, borderTop: onHour ? '1px solid var(--border-dim)' : '1px solid transparent' }}>
                      {onHour ? fmt12(h, m) : ''}
                    </td>
                    {days.map((d, dayIdx) => {
                      const key = slotIso(d.dateStr, rowIdx);
                      const slot = dayColumns[dayIdx]?.col[rowIdx] || null;
                      const showName = dayColumns[dayIdx]?.labelRow[rowIdx];
                      const mine = slot && slot.operatorId === user?.id;
                      const sel = selected.has(key);
                      let bg = 'transparent';
                      // colorFor may return a var(--…) reference or a hex value; color-mix
                      // applies the tint correctly for both (string concat like `${c}33`
                      // yields an invalid color for var() refs → renders transparent).
                      if (slot) bg = mine ? 'rgba(0,212,255,0.22)' : `color-mix(in srgb, ${colorFor(slot.operatorId)} 20%, transparent)`;
                      else if (sel) bg = 'rgba(0,255,136,0.3)';
                      const viewerTime = formatInTimeZone(new Date(key), viewerTz, 'EEE h:mm a');
                      return (
                        <td
                          key={d.dateStr}
                          onMouseDown={() => onCellDown(key, slot, mine)}
                          onMouseEnter={() => onCellEnter(key, slot)}
                          title={slot ? `${cap(slot.operatorName)} · your time ${viewerTime}` : `Book ${fmt12(h, m)} · your time ${viewerTime}`}
                          style={{
                            ...slotCellStyle,
                            borderTop: onHour ? '1px solid var(--border-dim)' : '1px solid var(--bg-elevated)',
                            background: bg,
                            cursor: slot ? (mine || isManager ? 'pointer' : 'default') : 'pointer',
                          }}
                        >
                          {slot && showName && (
                            <span style={{ fontSize: '13px', fontWeight: 700, color: mine ? 'var(--accent-cyan)' : 'var(--text-primary)', textAlign: 'center', display: 'block', lineHeight: 1.1 }}>
                              {cap(slot.operatorName)}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-muted)', flexShrink: 0 }}>
        Click an empty slot to book yourself · drag to book a range · click your own slot to remove it · “Repeat” copies this week forward
        {isManager && ' · as a manager you can remove anyone’s slot and use “Booking as” to assign others'}
      </div>
    </div>
  );
}

const thStyle = {
  position: 'sticky', top: 0, zIndex: 2,
  background: 'var(--bg-surface)',
  borderBottom: '1px solid var(--border-dim)',
  padding: '8px 4px',
  fontFamily: 'var(--font-display)', fontSize: '13px', fontWeight: 700,
  letterSpacing: '0.04em', textTransform: 'uppercase',
};
const timeCellStyle = {
  width: '70px', position: 'sticky', left: 0, zIndex: 1,
  background: 'var(--bg-surface)',
  padding: '0 8px', height: '26px',
  fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--text-primary)',
  textAlign: 'right', verticalAlign: 'top', whiteSpace: 'nowrap',
};
const slotCellStyle = {
  borderLeft: '1px solid var(--border-dim)',
  height: '26px', padding: '0 4px', verticalAlign: 'middle',
};
